import { useState, type FormEvent } from 'react'
import {
  emptyPropertyForm,
  propertyToFormValues,
  validateProperty,
  type Property,
  type PropertyFormValues,
} from './property-types'

interface PropertyFormDialogProps {
  property: Property | null
  isSaving: boolean
  saveError: string | null
  onClose: () => void
  onSave: (values: PropertyFormValues) => Promise<void>
}

export function PropertyFormDialog({ property, isSaving, saveError, onClose, onSave }: PropertyFormDialogProps) {
  const [values, setValues] = useState<PropertyFormValues>(() => property ? propertyToFormValues(property) : emptyPropertyForm)
  const [validationError, setValidationError] = useState<string | null>(null)

  const setField = <K extends keyof PropertyFormValues>(field: K, value: PropertyFormValues[K]) => {
    setValues((current) => ({ ...current, [field]: value }))
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const error = validateProperty(values)
    setValidationError(error)
    if (error) return
    await onSave(values)
  }

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !isSaving) onClose()
    }}>
      <section className="property-dialog" role="dialog" aria-modal="true" aria-labelledby="property-dialog-title">
        <div className="dialog-heading">
          <div>
            <p className="eyebrow">Property workspace</p>
            <h2 id="property-dialog-title">{property ? 'Edit property' : 'Add property'}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} disabled={isSaving} aria-label="Close">×</button>
        </div>
        <form className="property-form" onSubmit={(event) => void handleSubmit(event)}>
          <label className="field-wide">Property name
            <input autoFocus value={values.name} onChange={(event) => setField('name', event.target.value)} maxLength={160} required />
          </label>
          <label className="field-wide">Street address
            <input value={values.addressLine1} onChange={(event) => setField('addressLine1', event.target.value)} required />
          </label>
          <label>City
            <input value={values.city} onChange={(event) => setField('city', event.target.value)} required />
          </label>
          <label>State / region
            <input value={values.region} onChange={(event) => setField('region', event.target.value)} required />
          </label>
          <label>Postal code
            <input value={values.postalCode} onChange={(event) => setField('postalCode', event.target.value)} required />
          </label>
          <label>Country code
            <input value={values.countryCode} onChange={(event) => setField('countryCode', event.target.value)} maxLength={2} required />
          </label>
          <label className="field-wide">Status
            <select value={values.status} onChange={(event) => setField('status', event.target.value as PropertyFormValues['status'])}>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </label>
          {(validationError ?? saveError) && <p className="form-error field-wide" role="alert">{validationError ?? saveError}</p>}
          <div className="dialog-actions field-wide">
            <button className="secondary-button" type="button" onClick={onClose} disabled={isSaving}>Cancel</button>
            <button type="submit" disabled={isSaving}>{isSaving ? 'Saving…' : property ? 'Save changes' : 'Create property'}</button>
          </div>
        </form>
      </section>
    </div>
  )
}
